import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { Info, Loader2, Plus, Target } from "lucide-react";
import { deleteGoal } from "../../utilities/api";
import AppShell from "../layout/AppShell";
import { Button } from "../Button";
import { Card, CardContent } from "../card";
import GoalCard, { formatRate } from "./GoalCard";
import GoalFormModal, { DialogHeader, DialogShell } from "./GoalFormModal";
import { useGoalData } from "./useGoalData";

const ConfirmDeleteDialog = ({ goal, isDeleting, error, onCancel, onConfirm }) => {
	const label =
		goal?.type === "FINAL" ? "your final goal" : `your ${goal?.year} goal`;

	return (
		<DialogShell
			open={Boolean(goal)}
			onClose={onCancel}
			labelledBy="delete-goal-title"
			describedBy="delete-goal-description"
			className="sm:max-w-md"
		>
			<DialogHeader
				titleId="delete-goal-title"
				title="Delete goal?"
				descriptionId="delete-goal-description"
				description={`This permanently removes ${label}. Your transactions and portfolio value are not affected.`}
				onClose={onCancel}
			/>
			{error && (
				<div
					role="alert"
					className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
				>
					{error}
				</div>
			)}
			<div className="flex justify-end gap-2">
				<Button type="button" variant="outline" className="h-9" onClick={onCancel}>
					Cancel
				</Button>
				<Button
					type="button"
					variant="destructive"
					className="h-9"
					disabled={isDeleting}
					onClick={onConfirm}
					data-autofocus
				>
					{isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
					{isDeleting ? "Deleting..." : "Delete goal"}
				</Button>
			</div>
		</DialogShell>
	);
};

const GoalCardSkeleton = () => (
	<Card className="rounded-xl border-slate-200 bg-white shadow-sm">
		<CardContent className="space-y-5 p-5">
			<div className="h-5 w-32 animate-pulse rounded bg-slate-100" />
			<div className="h-24 animate-pulse rounded-lg bg-slate-100" />
			<div className="h-2.5 animate-pulse rounded-full bg-slate-100" />
			<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
				{[0, 1, 2, 3].map((key) => (
					<div key={key} className="h-10 animate-pulse rounded bg-slate-100" />
				))}
			</div>
		</CardContent>
	</Card>
);

const GoalsPage = () => {
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();
	const [modalOpen, setModalOpen] = useState(false);
	const [editingGoal, setEditingGoal] = useState(null);
	const [goalPendingDelete, setGoalPendingDelete] = useState(null);
	const [deleteError, setDeleteError] = useState("");
	const {
		isLoading,
		rows,
		currentYear,
		currentYearGoal,
		nextYearGoal,
		finalGoal,
		yearlyRows,
		historicalPercent,
		baselinePercent,
		usesMoneyWeighted,
	} = useGoalData();

	const removeMutation = useMutation(deleteGoal, {
		onSuccess: () => {
			queryClient.invalidateQueries("fetchGoals");
			setGoalPendingDelete(null);
		},
		onError: (err) =>
			setDeleteError(
				err?.response?.data?.error || err?.message || "Couldn't delete this goal"
			),
	});

	const openCreate = () => {
		setEditingGoal(null);
		setModalOpen(true);
	};
	const openEdit = (goal) => {
		setEditingGoal(goal);
		setModalOpen(true);
	};

	// The dashboard's empty-state link lands here with ?new=1 so "Set a goal"
	// opens the form instead of dropping the user on a page to hunt for it.
	useEffect(() => {
		if (searchParams.get("new") !== "1") return;
		setEditingGoal(null);
		setModalOpen(true);
		const next = new URLSearchParams(searchParams);
		next.delete("new");
		setSearchParams(next, { replace: true });
	}, [searchParams, setSearchParams]);

	// Everything not already shown as this year / next year / final.
	const otherYears = yearlyRows
		.filter((row) => row.goal.year !== currentYear && row.goal.year !== currentYear + 1)
		.sort((left, right) => left.goal.year - right.goal.year);

	const orderedGoals = [
		currentYearGoal && { row: currentYearGoal, context: "This investment year" },
		finalGoal && { row: finalGoal, context: "Long-range target" },
		nextYearGoal && { row: nextYearGoal, context: "Next investment year" },
		...otherYears.map((row) => ({
			row,
			context: row.goal.year < currentYear ? "Past year" : "Future year",
		})),
	].filter(Boolean);

	const hasAnyGoal = orderedGoals.length > 0;

	return (
		<AppShell
			title="Goals"
			description="What you need to invest each month to hit your targets."
			actions={
				<Button size="sm" onClick={openCreate}>
					<Plus className="mr-2 h-4 w-4" aria-hidden="true" />
					New goal
				</Button>
			}
		>
			{isLoading && (
				<div className="grid gap-5 lg:grid-cols-2">
					<GoalCardSkeleton />
					<GoalCardSkeleton />
				</div>
			)}

			{!isLoading && !hasAnyGoal && (
				<Card className="rounded-xl border-slate-200 bg-white shadow-sm">
					<CardContent className="flex flex-col items-center gap-3 px-6 py-12 text-center">
						<span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
							<Target className="h-5 w-5 text-slate-600" aria-hidden="true" />
						</span>
						<h2 className="text-base font-semibold text-slate-900">No goals yet</h2>
						<p className="max-w-md text-sm leading-6 text-slate-500">
							Set a target for this investment year or a long-range final goal, and
							this page will show how much you need to invest each month to reach it.
						</p>
						<Button size="sm" className="mt-1" onClick={openCreate}>
							<Plus className="mr-2 h-4 w-4" aria-hidden="true" />
							New goal
						</Button>
					</CardContent>
				</Card>
			)}

			{!isLoading && hasAnyGoal && (
				<div className="grid items-start gap-5 lg:grid-cols-2">
					{orderedGoals.map(({ row, context }) => (
						<GoalCard
							key={row.goal.id}
							row={row}
							contextLabel={context}
							usesMoneyWeighted={usesMoneyWeighted}
							onEdit={openEdit}
							onDelete={(goal) => {
								setDeleteError("");
								setGoalPendingDelete(goal);
							}}
						/>
					))}
				</div>
			)}

			{!isLoading && hasAnyGoal && (
				<div className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
					<Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
					<div className="text-sm leading-6 text-slate-500">
						<p className="font-medium text-slate-900">How these projections work</p>
						<p className="mt-1">
							Every goal is solved twice, because the answer depends entirely on
							which growth rate you assume. The{" "}
							<span className="font-medium text-slate-700">fair estimate</span>{" "}
							compounds a plain long-run {formatRate(baselinePercent)}; it drives
							the headline figure, the status badge and the projected part of each
							bar.{" "}
							<span className="font-medium text-slate-700">Your past return</span>{" "}
							compounds {formatRate(historicalPercent)}
							{usesMoneyWeighted
								? " — the annualized return on the money you have actually invested, which credits capital added recently only for the time it has been at work"
								: " — its compound annual return since the first trade"}
							. A stretch of exceptional years is not a forecast, so plan against the
							fair estimate and treat the second figure as the upside.
						</p>
						<p className="mt-2">
							Both assume you invest at the end of each month. Real returns vary a
							lot year to year: if growth comes in lower than assumed, you will need
							to invest more each month than shown.
						</p>
					</div>
				</div>
			)}

			<GoalFormModal
				open={modalOpen}
				goal={editingGoal}
				goals={rows.map((row) => row.goal)}
				onClose={() => {
					setModalOpen(false);
					setEditingGoal(null);
				}}
			/>

			<ConfirmDeleteDialog
				goal={goalPendingDelete}
				isDeleting={removeMutation.isLoading}
				error={deleteError}
				onCancel={() => {
					setGoalPendingDelete(null);
					setDeleteError("");
				}}
				onConfirm={() => removeMutation.mutate(goalPendingDelete.id)}
			/>
		</AppShell>
	);
};

export default GoalsPage;
